import cn from "clsx";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

import { ConfigSummaryItem } from "@/features/product/components/hooks/use-product-config-summary";

type ProductSummaryProps = {
  configSummary: ConfigSummaryItem[];
  spine?: ConfigSummaryItem;
  className?: string;
  spineClassName?: string;
};
type SummaryItemWithPrefix = ConfigSummaryItem & { shouldHideSplitter?: boolean };

export const ProductSummary = ({
  configSummary,
  spine,
  className = "",
  spineClassName = "",
}: ProductSummaryProps) => {
  // Make local copy of configSummary to add prefix to each item
  const [summaryItems, setSummaryItems] = useState<SummaryItemWithPrefix[] | null>(null);
  const t = useTranslations("attributes");

  // Assign ref to attributes container and remove prefix from items which stating from new line
  const attrContainerRef = useCallback(
    (node: HTMLParagraphElement) => {
      if (node !== null) {
        const children = node.children;
        setSummaryItems(() =>
          (configSummary as SummaryItemWithPrefix[]).map((item, index) => {
            const child = children[index] as unknown as HTMLSpanElement;
            if (!child) return item;
            const nextChild = children[index + 1] as unknown as HTMLSpanElement | undefined;
            const childSize = child.getBoundingClientRect();
            const nextChildSize = nextChild?.getBoundingClientRect();
            if (nextChildSize && nextChildSize?.top > childSize.top) {
              item.shouldHideSplitter = true;
              return item;
            }
            return item;
          }),
        );
      }
    },
    [configSummary],
  );

  useEffect(() => {
    setSummaryItems(null);
  }, [configSummary]);

  return (
    <>
      <p ref={attrContainerRef} className={cn("text-grey-90 flex flex-wrap", className)}>
        {(summaryItems ?? (configSummary as SummaryItemWithPrefix[])).map((item, index, items) => {
          const keyLower = item.key.toLowerCase();
          const translatedKey = t(keyLower);
          const displayKey = translatedKey !== `attributes.${keyLower}` ? translatedKey : item.key;

          return (
            <span key={index} className="block w-full truncate md:w-auto">
              <b>
                {displayKey}:{`\u00A0`}
              </b>
              {item.value}
              <span
                className={cn({
                  invisible: index === items.length - 1 || item?.shouldHideSplitter,
                })}
              >
                {"\u00A0|\u00A0"}
              </span>
            </span>
          );
        })}
      </p>
      {spine?.key &&
        (() => {
          const keyLower = spine.key.toLowerCase();
          const translatedKey = t(keyLower);
          const displayKey = translatedKey !== `attributes.${keyLower}` ? translatedKey : spine.key;

          return (
            <p className={cn("text-grey-90", spineClassName)}>
              <b>
                {displayKey}:{`\u00A0`}
              </b>
              {spine.value}
            </p>
          );
        })()}
    </>
  );
};
